// Whop Elements owns payment/3DS state. Only load failures reach onError;
// declines and retries remain inside the element. Fulfillment stays in webhooks.
const ELEMENTS_URL = 'https://cdn.whop.com/elements/amber/elements.js';
let loadingElements;

export function loadWhopElements() {
  if (typeof window.WhopElements === 'function') return Promise.resolve(window.WhopElements);
  if (loadingElements) return loadingElements;
  loadingElements = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = ELEMENTS_URL;
    script.async = true;
    script.setAttribute('data-whop-elements', '');
    const timer = setTimeout(() => fail(), 20000);
    const fail = () => {
      clearTimeout(timer);
      script.remove();
      reject(new Error('Unable to load the secure payment form. Please try again.'));
    };
    script.onerror = fail;
    script.onload = () => {
      clearTimeout(timer);
      if (typeof window.WhopElements !== 'function') return fail();
      resolve(window.WhopElements);
    };
    document.head.appendChild(script);
  }).catch((error) => {
    loadingElements = null;
    throw error;
  });
  return loadingElements;
}

// The same-tab navigation Elements performs after a payment, for a fulfilled
// order whose redirect never arrived. Only our own confirmation page qualifies.
export function openWhopReturnPage(returnUrl) {
  if (typeof window === 'undefined' || typeof returnUrl !== 'string' || !returnUrl) return;
  try {
    const target = new URL(returnUrl, window.location.origin);
    if (target.origin !== window.location.origin || target.pathname !== '/payment-successful'
      || !target.searchParams.get('checkoutContext')) return;
    window.location.assign(target.href);
  } catch (_) { /* an unusable return address confirms nothing */ }
}

// Use the same neutral palette across the hosted checkout and app embeds.
// Style only documented parts; keep Whop's wallet availability, focus states,
// terms, currency selector and validation visible.
export function whopCheckoutColors(dark = false) {
  return {
    ink: dark ? '#FFFFFF' : '#040404',
    surface: dark ? '#1C1C1C' : '#FFFFFF',
    secondary: dark ? '#B3B3B3' : '#616161',
    inset: dark ? '#282828' : '#F5F5F5',
    border: '#808080',
  };
}

export function whopCheckoutAppearance(dark = false) {
  const palette = whopCheckoutColors(dark);
  return {
    theme: { appearance: dark ? 'dark' : 'light', accentColor: 'gray', grayColor: 'gray' },
    variables: { '--radius': '12px', '--default-font-family': '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    classes: {
      'whop-Checkout': {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        color: palette.ink,
        backgroundColor: palette.surface,
      },
      'whop-CheckoutLayout': { gap: '24px' },
      'whop-CheckoutPayButton': { backgroundColor: palette.ink, color: palette.surface, borderRadius: '10px', minHeight: '48px', fontWeight: '600' },
      'whop-CheckoutPrice': { letterSpacing: '-0.02em' },
      'whop-CheckoutTerms': { fontSize: '12px', lineHeight: '18px' },
      'whop-EmailInput': { borderRadius: '10px', minHeight: '44px' },
      'whop-CardFieldInput': { borderRadius: '10px', minHeight: '44px' },
    },
  };
}

const AUTO_KEY = 'gab:whop:checkout-opt-in';
const MAX_AGE = 2 * 60 * 60 * 1000;

export function saveWhopCheckoutOptIn({ contextId, userId, authorization, returnPath }) {
  try {
    if (!contextId || !userId) {
      window.sessionStorage.removeItem(AUTO_KEY);
    } else {
      const previous = JSON.parse(window.sessionStorage.getItem(AUTO_KEY) || 'null');
      const path = returnPath || (previous?.contextId === contextId && previous?.userId === userId ? previous.returnPath : undefined);
      window.sessionStorage.setItem(AUTO_KEY, JSON.stringify({ contextId, userId, authorization, returnPath: path, createdAt: Date.now() }));
    }
    return true;
  } catch { return false; }
}

export function whopCheckoutReturnPath(contextId, userId) {
  try {
    const record = JSON.parse(window.sessionStorage.getItem(AUTO_KEY) || 'null');
    if (!record || record.contextId !== contextId || record.userId !== userId
      || !Number.isFinite(record.createdAt) || Date.now() - record.createdAt > MAX_AGE || record.createdAt > Date.now()) return '/app';
    const url = new URL(record.returnPath, window.location.origin);
    if (url.origin !== window.location.origin || !record.returnPath?.startsWith('/')
      || url.pathname === '/payment-successful') return '/app';
    return url.pathname + url.search;
  } catch { return '/app'; }
}

// Consume once, after a server-confirmed purchase by the same signed-in user.
// A different tab/account/checkout cannot authorize recurring credit charges.
export function consumeWhopCheckoutOptIn(contextId, userId) {
  try {
    const record = JSON.parse(window.sessionStorage.getItem(AUTO_KEY) || 'null');
    if (!record || record.contextId !== contextId || record.userId !== userId) return null;
    // Keep the return destination across refreshes; consume only the consent.
    window.sessionStorage.setItem(AUTO_KEY, JSON.stringify({ ...record, authorization: null }));
    if (!Number.isFinite(record.createdAt) || Date.now() - record.createdAt > MAX_AGE || record.createdAt > Date.now()) return null;
    return record.authorization;
  } catch { return null; }
}

export async function applyWhopCheckoutOptIn({ contextId, userId, payment, saveSettings, applySettings }) {
  if (payment?.status !== 'completed' || payment.checkoutType !== 'credits' || !payment.paymentId) return '';
  const authorization = consumeWhopCheckoutOptIn(contextId, userId);
  if (!authorization) return '';
  try {
    const response = await saveSettings({ ...authorization, afterPurchase: true, afterPurchasePaymentId: payment.paymentId });
    if (!response.autoTopup) throw new Error('Settings not confirmed');
    applySettings(response.autoTopup);
    return 'Auto top-up is enabled.';
  } catch (_) {
    return 'Your credits are ready. Auto top-up could not be enabled; review it in Credits settings.';
  }
}
